// Loading the Alexa name namespace, and keeping it current.
//
// The rule and the pure logic are in ./alexaNames; this is only where the three
// lists come from and how often. Split so the logic stays testable without a
// server and the loading stays in one place.
//
// ⚠️ MEMOISED, BECAUSE THE CHECK RUNS ON A DIALOG. Building the index is three
// requests on desktop (four with locations) (free on the phone — see api.getAllOutputsForUser), and a
// customer renaming four buttons on a panel opens the dialog four times. Without
// the memo that is twelve requests to answer the same question.
//
// ⚠️ AND INVALIDATED ON EVERY RENAME, which matters more than the memo does: the
// second of two renames must see the first, or a customer can set two buttons to
// the same name inside the TTL and the whole feature is decorative. `api.js`
// calls `invalidateNameIndex` from the same place it patches the offline cache.

import {
  getAllOutputsForUser, getAllDevicesForUser, getAllRoomsForUser, getLocations,
} from '../api/api';
import { buildNameIndex } from './alexaNames';

// Long enough to cover one operator working through a panel, short enough that
// a change made on another device shows up without a restart.
const TTL_MS = 60 * 1000;

let cached = null;   // { userId, at, index }
let inflight = null; // { userId, promise } — two dialogs opening at once share one load

export function invalidateNameIndex() {
  cached = null;
  inflight = null;
}

const rows = (res) => (res && Array.isArray(res.data) ? res.data : []);

/**
 * The account's name index.
 *
 * ⚠️ RETURNS null RATHER THAN THROWING when the lists cannot be read. A rename
 * must not be blocked because the phone is offline or an endpoint is down —
 * Alexa is not reachable then either, and refusing to save would turn a voice
 * nicety into a broken app. Callers treat null as "cannot check, allow".
 */
export async function loadNameIndex(userId) {
  if (userId == null) return null;
  const uid = String(userId);

  if (cached && cached.userId === uid && Date.now() - cached.at < TTL_MS) return cached.index;
  if (inflight && inflight.userId === uid) return inflight.promise;

  const promise = (async () => {
    try {
      const [outputs, devices, rooms, locations] = await Promise.all([
        getAllOutputsForUser(userId).then(rows),
        getAllDevicesForUser(userId).then(rows),
        getAllRoomsForUser(userId).then(rows).catch(() => []),
        // A Common device (no room) is spoken "<Location> <name>".
        getLocations(userId).then(rows).catch(() => []),
      ]);
      // No outputs AND no devices means the read failed or the account is
      // empty; either way there is nothing to check against, and claiming an
      // empty namespace would let every duplicate through silently.
      if (!outputs.length && !devices.length) return null;
      const index = buildNameIndex({
        outputs, devices, rooms, locations,
      });
      cached = { userId: uid, at: Date.now(), index };
      return index;
    } catch (e) {
      return null;
    } finally {
      inflight = null;
    }
  })();

  inflight = { userId: uid, promise };
  return promise;
}

export default loadNameIndex;
